/* eslint-disable no-loop-func */

import React, { useEffect, useMemo, useRef, useState } from "react";

const TEMP_COLOR = "#80DEEA";

const calculateRadius = (searchVolume: number, isCluster = false) => {
  const scale = isCluster ? 0.3 : 1.2;
  return Math.sqrt(searchVolume) * scale;
};

const BubbleClusterChart = ({ bubbleChartData }: any) => {
  const [tooltip, setTooltip] = useState<{
    x: number;
    y: number;
    content: string;
  } | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);

  // Dynamically adjust width based on window size
  const [svgWidth, setSvgWidth] = useState(window.innerWidth);
  const svgHeight = 1000; // Fixed height for now

  useEffect(() => {
    const handleResize = () => setSvgWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Process bubble chart data
  const data = useMemo(() => {
    return bubbleChartData.map((item: any) => {
      const uniqueClusters = [...new Set(item.clusters)];
      return {
        topic: item.topicName,
        searchVolume: Math.floor(Math.random() * 5000) + 2000,
        topicCoverage: +(Math.random().toFixed(2)),
        clusters: uniqueClusters.map((label) => ({
          label,
          searchVolume: Math.floor(Math.random() * 1500) + 500,
        })),
        keywordCount: new Set(item.keywords).size,
      };
    });
  }, [bubbleChartData]);

  const topicPositions = useMemo(() => {
    const positions: { x: number; y: number; r: number }[] = [];

    const isOverlapping = (x: number, y: number, r: number) => {
      return positions.some((pos) => {
        const dx = pos.x - x;
        const dy = pos.y - y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance < pos.r + r + 20; // Buffer to prevent overlap
      });
    };

    data.forEach((topic: any) => {
      const r = calculateRadius(topic.searchVolume);
      const padding = 20;
      let x, y;
      let attempts = 0;
      const maxAttempts = 1000;

      do {
        x = Math.random() * (svgWidth - 2 * r - 2 * padding) + r + padding;
        y = Math.random() * (svgHeight - 2 * r - 2 * padding) + r + padding;
        attempts++;
        if (attempts > maxAttempts) {
          break;
        }
      } while (isOverlapping(x, y, r));

      positions.push({ x, y, r });
    });

    return positions;
  }, [data, svgWidth, svgHeight]);

  const showTooltip = (
    e: React.MouseEvent<SVGCircleElement, MouseEvent>,
    content: string
  ) => {
    if (svgRef.current) {
      const svgRect = svgRef.current.getBoundingClientRect();
      setTooltip({
        x: e.clientX - svgRect.left + 16,
        y: e.clientY - svgRect.top + 16,
        content,
      });
    }
  };

  const hideTooltip = () => setTooltip(null);

  const renderClustersInside = (
    parentX: number,
    parentY: number,
    parentR: number,
    clusters: any[]
  ) => {
    const angleStep = (2 * Math.PI) / clusters.length;
    const clusterDistance = parentR * 0.7;  // Adjust distance to prevent overlap
    const maxClusterRadius = parentR * 0.4; // Max radius for clusters

    // To avoid overlaps between clusters
    const clusterBuffer = 10; // Buffer space between clusters
    const clusterPositions: any = [];

    clusters.forEach((c, index) => {
      const angle = index * angleStep;
      const clusterRadius = Math.min(
        calculateRadius(c.searchVolume, true),
        maxClusterRadius
      );

      let cx = parentX + clusterDistance * Math.cos(angle);
      let cy = parentY + clusterDistance * Math.sin(angle);

      let attempts = 0;
      const maxAttempts = 100; // Allow multiple attempts to prevent overlap

      while (
        clusterPositions.some(
          (pos: any) => {
            const dx = pos.cx - cx;
            const dy = pos.cy - cy;
            const distance = Math.sqrt(dx * dx + dy * dy);
            return distance < pos.clusterRadius + clusterRadius + clusterBuffer;
          }
        ) && attempts < maxAttempts
      ) {
        // Adjust the position and recheck
        cx = parentX + clusterDistance * Math.cos(angle + Math.random() * 0.2);
        cy = parentY + clusterDistance * Math.sin(angle + Math.random() * 0.2);
        attempts++;
      }

      clusterPositions.push({ cx, cy, clusterRadius });
    });

    return clusterPositions.map((pos: any, index: number) => (
      <circle
        key={index}
        cx={pos.cx}
        cy={pos.cy}
        r={pos.clusterRadius}
        fill="#AEDFF7"
        stroke="#333"
        strokeWidth={1}
        onMouseEnter={(e) => {
          e.stopPropagation(); // prevent topic hover conflict
          showTooltip(
            e,
            `<div style="font-family: sans-serif">
              <strong>${clusters[index].label}</strong><br/>
              Search Volume: ${clusters[index].searchVolume}
            </div>`
          );
        }}
        onMouseLeave={(e) => {
          e.stopPropagation();
          hideTooltip();
        }}
      />
    ));
  };

  return (
    <div
      style={{
        position: "relative",
        height: "700px",
        overflowY: "auto",
        border: "1px solid #ccc",
      }}
    >
      <svg ref={svgRef} width="100%" height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} preserveAspectRatio="xMidYMid meet">
        {data.map((topic: any, index: number) => {
          const { x, y, r } = topicPositions[index];

          return (
            <g key={index}>
              <circle
                cx={x}
                cy={y}
                r={r}
                fill={TEMP_COLOR}
                opacity={0.75}
                stroke="#333"
                strokeWidth={1}
                onMouseEnter={(e) =>
                  showTooltip(
                    e,
                    `<div style="font-family: sans-serif">
                      <div style="color: #1E5F74; font-weight: 600; font-size: 16px; margin-bottom: 6px; border-bottom: 2px solid #1E5F74; padding-bottom: 4px;">
                        ${topic.topic}
                      </div>
                      <div style="color: #666; font-size: 14px; margin-bottom: 4px;">
                        KEYWORDS • ${topic.keywordCount}
                      </div>
                      <div style="color: #666; font-size: 14px; margin-bottom: 4px;">
                        CLUSTER VOL • ${topic.searchVolume.toLocaleString()}
                      </div>
                      <div style="color: #666; font-size: 14px;">
                        TOPIC COVERAGE • ${(topic.topicCoverage * 100).toFixed(0)}%
                      </div>
                    </div>`
                  )
                }
                onMouseLeave={hideTooltip}
              />
              {renderClustersInside(x, y, r, topic.clusters)}
            </g>
          );
        })}
      </svg>

      {tooltip && (
        <div
          style={{
            position: "absolute",
            top: tooltip.y,
            left: tooltip.x,
            background: "#fff",
            borderRadius: "12px",
            padding: "12px 16px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            pointerEvents: "none",
            zIndex: 1000,
            maxWidth: "240px",
            fontSize: "13px",
            lineHeight: "1.5",
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}
    </div>
  );
};

export default BubbleClusterChart;
